// The rate card check figure on the office timesheet page (Session ANH).
//
// WHAT IT SAYS AND WHAT IT NEVER SAYS. This is what the engineer's subcontractor's rate card (Session
// AUU: a card per subcontractor, held as data with an effective date, read through the engineer's link
// to their subcontractor) says the engineer's week comes to, so the office can hold the subcontractor's
// own weekly invoice against it before paying. It is labelled as a check figure everywhere it appears
// and never as an amount the platform pays, drafts or promises: the subcontractor still sends one
// invoice per engineer per week by hand.
//
// ADMIN ONLY. The page renders this only for an admin (the payroll CSV's own access pattern, and the
// server refuses the read to anyone else). The per-engineer figure is PRIMARY, since each engineer's
// invoice is checked against it; the rollup across the engineers who trade as the same
// subcontractor is a secondary line for the Director's own P&L view, labelled as a rollup.
//
// EVERYTHING THE CARD COULD NOT PRICE IS NAMED. An exception that needs office review is shown in
// amber with the sentence the server wrote; one that is merely "not on the card" (office time,
// training) is listed neutrally so the office can see the minutes are accounted for.
//
// Session AUV (design system Session 8): the three local date formatters are gone for lib/format.ts ("Sat 5 Sep", and a
// long-month date prints "5 Oct 2026"). Presentation only: ADMIN ONLY and every figure, line and refusal are as before
// (timesheet pay visibility is exactly as it was, D57 open).
import { useState } from "react";
import { ApiError, api } from "../lib/api";
import type { PayeHours, TimesheetPayException, TimesheetPayLine, TimesheetPayReport } from "../lib/api";
import { ConfirmButton } from "./ConfirmButton";
import { formatDurationHm } from "../lib/timeOnSite";
import { formatDate, formatWeekdayShort } from "../lib/format";

export function formatGbp(pence: number): string {
  const sign = pence < 0 ? "-" : "";
  const abs = Math.abs(pence);
  return `${sign}GBP ${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

const BASIS_COPY: Record<TimesheetPayReport["group"]["basis"], string> = {
  subcontractor_link: "keyed on the engineers linked to this subcontractor record.",
  engineer_alone: "",
};

// A line's amount column: the rate beside it where one applies, so the office can check the
// arithmetic without a calculator ("2h 30m at GBP 75.00/h").
// Session RC1: no flat sum is ever shown "/h". The technician block is per visit, a modernisation or installation day per day,
// time past the block per hour, and time past the block not agreed carries no rate at all (its note says why). An uplifted
// call-out or repair line ("Repair, Time and a half (x1.5)") is an hourly rate already uplifted by the server.
export function lineRate(line: TimesheetPayLine): string {
  if (line.ratePence == null) return "";
  switch (line.kind) {
    case "standby":
      return `${formatGbp(line.ratePence)}/wk`;
    case "servicing":
    case "site_survey":
    case "tech_visit_block":
      return `${formatGbp(line.ratePence)}/visit`;
    case "day_rate_cap":
      return `day rate ${formatGbp(line.ratePence)}`;
    case "day_rate":
    case "modernisation_day":
      return `${formatGbp(line.ratePence)}/day`;
    case "tech_visit_unpaid":
      return "";
    default:
      return `${formatGbp(line.ratePence)}/h`;
  }
}

// Session RC1: a PAYE engineer is paid through payroll, which runs outside the platform. Hours by band, never a figure.
export function PayeHoursPanel({ hours, engineerName }: { hours: PayeHours; engineerName: string }) {
  const rows: { label: string; mins: number }[] = [
    { label: "Contracted hours", mins: hours.contractedMins },
    { label: "Overtime at the normal rate", mins: hours.normalOvertimeMins },
    { label: "Time and a half (x1.5)", mins: hours.timeAndHalfMins },
    { label: "Double time (x2)", mins: hours.doubleTimeMins },
  ];
  return (
    <section aria-label="PAYE hours" className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">Hours by band</p>
      <p className="mt-1 text-sm text-ele-body">
        <span className="text-ele-strong">{engineerName}</span>. Paid through payroll: hours only.
      </p>
      <dl className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 text-sm">
        {rows.map((r) => (
          <div key={r.label} className="contents">
            <dt className="text-ele-body">{r.label}</dt>
            <dd className="text-right text-ele-strong">{formatDurationHm(r.mins)}</dd>
          </div>
        ))}
        <dt className="border-t border-ele-purple-accent/40 pt-1 text-ele-strong">Total</dt>
        <dd className="border-t border-ele-purple-accent/40 pt-1 text-right font-heading text-ele-strong" data-testid="paye-total">
          {formatDurationHm(hours.totalMins)}
        </dd>
      </dl>
      <ul className="mt-3 space-y-1 text-xs text-ele-body">
        <li>Standby this week: {hours.standbyWeek ? "yes" : "no"}.</li>
        <li>
          Out-of-hours calls attended: {hours.outOfHoursCalls}
          {hours.callMinimumAddedMins > 0 ? `, with ${formatDurationHm(hours.callMinimumAddedMins)} added so each counts at least an hour (already in the bands above)` : ""}.
        </li>
        {hours.untimedEntries > 0 && (
          <li className="text-[var(--ele-amber-text-deep)]">
            {hours.untimedEntries} weekday entr{hours.untimedEntries === 1 ? "y has" : "ies have"} no clock times, so counted as weekday time. Check {hours.untimedEntries === 1 ? "it" : "them"}.
          </li>
        )}
      </ul>
    </section>
  );
}

export function TimesheetPayPanel({
  report,
  canDraftJournal,
  canEditBooking,
  onChanged,
}: {
  report: TimesheetPayReport;
  // The office may press Draft Xero journal only on an APPROVED week, and only an admin sees the
  // panel at all; the server enforces both again.
  canDraftJournal: boolean;
  // Whether the two-man booking may be recorded from here: any week not yet approved, since the
  // figure moves with it and an approved week's figure is final until reopened.
  canEditBooking: boolean;
  onChanged: () => void;
}) {
  const { pay, group, timesheet } = report;
  const [journalBusy, setJournalBusy] = useState(false);
  const [journalError, setJournalError] = useState<string | null>(null);
  const [journalPrompt, setJournalPrompt] = useState<{ count: number; message: string } | null>(null);
  const [journalDone, setJournalDone] = useState<{ journalId: string; amountPence: number } | null>(null);

  const [clearBusy, setClearBusy] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  const reviewExceptions = pay.exceptions.filter((x) => x.needsReview);
  const infoExceptions = pay.exceptions.filter((x) => !x.needsReview);
  // The STATE is the server's: a live draft attempt, a stuck one, or a journal. Never re-derived here.
  const journalState = timesheet.xeroJournalState;
  const journalDrafted = journalState === "drafted";
  const journalStale = journalDrafted && timesheet.xeroJournalAmountPence != null && timesheet.xeroJournalAmountPence !== pay.totalPence;

  async function clearJournal() {
    if (!timesheet.timesheetId) return;
    setClearBusy(true);
    setClearError(null);
    try {
      await api.timesheets.clearXeroJournal(timesheet.timesheetId);
      setJournalDone(null);
      onChanged();
    } catch (err) {
      setClearError(err instanceof ApiError && err.message ? err.message : "Could not release the journal record. Please try again.");
    } finally {
      setClearBusy(false);
    }
  }

  async function draftJournal(acknowledged: boolean) {
    if (!timesheet.timesheetId) return;
    setJournalBusy(true);
    setJournalError(null);
    try {
      const res = await api.timesheets.draftXeroJournal(timesheet.timesheetId, acknowledged ? { acknowledgeExceptions: true } : undefined);
      setJournalPrompt(null);
      setJournalDone({ journalId: res.journalId, amountPence: res.amountPence });
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.body?.error === "review_needed") {
        const count = err.body.reviewCount;
        setJournalPrompt({ count: typeof count === "number" ? count : 0, message: err.message });
      } else {
        setJournalError(err instanceof ApiError && err.message ? err.message : "Could not draft the journal in Xero. Please try again.");
      }
    } finally {
      setJournalBusy(false);
    }
  }

  if (report.payeHours) return <PayeHoursPanel hours={report.payeHours} engineerName={pay.engineerName} />;

  return (
    <section aria-label="Rate card check figure" className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">Rate card check figure</p>
      <p className="mt-1 text-sm text-ele-body">
        What {group.subcontractorName ? <>{group.subcontractorName}&apos;s rate card</> : "the subcontractor's rate card"} says this week comes to, from the hours on this
        timesheet. Check <span className="text-ele-strong">{pay.engineerName}</span>&apos;s invoice against it. Nothing is paid automatically: they invoice by hand, one
        invoice per engineer per week.
        {pay.rateCardSource ? <span className="block text-xs">Priced from {pay.rateCardSource}.</span> : null}
      </p>
      {/* Session ANI. Said here rather than left to be discovered, because the hours in this panel can
          differ from the ones the rest of the page shows, and an office manager comparing the two needs
          to know that is the rule working rather than a fault.
          THE BOUND IS PER ENTRY, NOT PER PANEL (Session ANJ, the Fable audit of ANI). The first draft
          said "the hours shown here can differ by up to a quarter of an hour", which is true of each
          priced LINE and false of the day summary ("8h 30m counted toward the 8 hours") and the week
          total printed in this same panel, both of which are SUMS of rounded lines: four one-hour
          stints each logged 07:30 into a quarter (09:07:30 to 10:07:29 and so on) price as 45 minutes
          apiece, so the day reads 3h here against 4h in the entry list below, a full hour apart. An
          office manager told "a quarter of an hour" and shown an hour would conclude the panel is
          broken. Executed and confirmed by two independent reviewers before the wording was changed.
          "BELOW" IS LOAD-BEARING AND WAS WRONG IN THE FIRST DRAFT: TimesheetDetailPage renders this
          panel FIRST, so "Where the hours went", the day-by-day entry list and the totals all sit
          under it and there are no hours above it at all. If this panel is ever moved, move the word. */}
      <p className="mt-1 text-xs text-ele-body">
        Times are rounded to the nearest 15 minutes before the rate card is applied, rounding up from 7 minutes 30 seconds. So each priced line can differ by up
        to a quarter of an hour from the logged entry it comes from, and a day or week total by up to a quarter of an hour for each entry in it, compared with the
        logged hours further down this page. The entries themselves keep the times exactly as they were recorded.
      </p>

      {pay.rateCardEngineer == null ? (
        <p role="status" className="mt-3 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {pay.exceptions.find((x) => x.code === "no_rate_card")?.message ?? `There is no rate card for ${pay.engineerName}, so nothing here is priced.`}
        </p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <p>
              <span className="font-menu text-xs uppercase tracking-widest text-ele-blue">{pay.engineerName}</span>
              <span className="ml-3 font-heading text-2xl text-ele-green" data-testid="pay-total">
                {formatGbp(pay.totalPence)}
              </span>
            </p>
            {pay.unpricedMinutes > 0 && (
              <p className="text-xs text-[var(--ele-amber-text-deep)]">{formatDurationHm(pay.unpricedMinutes)} on this timesheet is not priced (see below).</p>
            )}
          </div>

          {/* Session AUZ: the Labour Only reading. The status in force on the week's days and the card's deduction are
              both shown, so the office can see WHY the figure is what it is: applied (the deduction and the status),
              Bona Fide, "Labour Only rate not set" (a notice, the figure is the Bona Fide figure), or an agreement
              that predates clause 12.5 (a warning, the deduction is deliberately not applied). */}
          {pay.labourOnly && pay.labourOnly.reason !== "no_priced_days" && (
            <div
              role="status"
              data-testid="labour-only"
              className={`mt-3 rounded-ele border px-3 py-2 text-sm ${pay.labourOnly.reason === "deduction_not_set" || pay.labourOnly.reason === "agreement_earlier_version" ? "border-ele-amber-line bg-ele-surface text-ele-strong" : "border-ele-purple-accent bg-ele-surface text-ele-body"}`}
            >
              <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">
                {pay.labourOnly.reason === "applied"
                  ? `Labour Only rates applied${pay.labourOnly.deductionLabel ? `, ${pay.labourOnly.deductionLabel} off` : ""}`
                  : pay.labourOnly.reason === "bona_fide"
                    ? "Bona Fide rates"
                    : pay.labourOnly.reason === "no_status_recorded"
                      ? "Bona Fide rates, no status recorded"
                    : pay.labourOnly.reason === "deduction_not_set"
                      ? "Labour Only rate not set"
                      : "Labour Only status, earlier agreement"}
              </p>
              <p className="mt-1">
                Status in force this week: <span className="text-ele-strong">{pay.labourOnly.statusLabel}</span>. Labour Only deduction on the card:{" "}
                <span className="text-ele-strong">{pay.labourOnly.deductionLabel ?? "not set"}</span>.
              </p>
              <p className="mt-1 text-xs">{pay.labourOnly.message}</p>
            </div>
          )}

          {reviewExceptions.length > 0 && (
            <div role="status" className="mt-3 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-3 text-sm">
              <p className="font-menu text-xs uppercase tracking-widest text-[var(--ele-amber-text-deep)]">
                {reviewExceptions.length === 1 ? "1 item needs office review" : `${reviewExceptions.length} items need office review`}
              </p>
              <ul className="mt-2 space-y-2">
                {reviewExceptions.map((x, i) => (
                  <ExceptionRow key={i} exception={x} canEdit={canEditBooking} onChanged={onChanged} />
                ))}
              </ul>
            </div>
          )}

          {pay.days.length === 0 && !pay.standby.applies && <p className="mt-3 text-sm text-ele-body opacity-70">No priced hours this week.</p>}

          {pay.days.map((day) => (
            <div key={day.date} className="mt-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-heading text-sm uppercase tracking-wide text-ele-blue">{formatWeekdayShort(day.date)}</p>
                <p className="text-xs text-ele-body">
                  {day.countedMinutes > 0 && <span>{formatDurationHm(day.countedMinutes)} counted toward the 8 hours</span>}
                  {day.cappedAtPence != null && <span> · capped at the day rate</span>}
                  {day.overtimeMinutes > 0 && <span> · {formatDurationHm(day.overtimeMinutes)} past 8 hours</span>}
                  {day.orderingUncertain && <span className="text-[var(--ele-amber-text-deep)]"> · order uncertain</span>}
                </p>
              </div>
              <ul className="mt-1 divide-y divide-ele-purple-accent/40">
                {day.lines.map((line, i) => (
                  <LineRow key={i} line={line} />
                ))}
              </ul>
              <p className="mt-1 text-right text-sm text-ele-strong">
                Day: <span className="font-heading">{formatGbp(day.totalPence)}</span>
              </p>
            </div>
          ))}

          {pay.standby.applies && (
            <div className="mt-3">
              <p className="font-heading text-sm uppercase tracking-wide text-ele-blue">Week</p>
              <ul className="mt-1">
                {pay.lines
                  .filter((l) => l.kind === "standby")
                  .map((line, i) => (
                    <LineRow key={i} line={line} />
                  ))}
              </ul>
            </div>
          )}

          {infoExceptions.length > 0 && (
            <div className="mt-3 text-xs text-ele-body">
              <p className="font-menu uppercase tracking-widest">Not on the rate card</p>
              <ul className="mt-1 space-y-1">
                {infoExceptions.map((x, i) => (
                  <li key={i}>
                    {x.date ? `${formatWeekdayShort(x.date)}: ` : ""}
                    {x.minutes != null ? `${formatDurationHm(x.minutes)}. ` : ""}
                    {x.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* No rollup for an engineer who stands alone: there is nothing to roll up, and a green
          "Combined GBP 0.00" under an amber "not on the card" would read as a figure. */}
      {group.basis !== "engineer_alone" && (
      <div className="mt-4 border-t border-ele-purple-accent/60 pt-3">
        <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">
          {group.subcontractorName ? `${group.subcontractorName} this week, rollup` : "This week, rollup"}
        </p>
        <p className="mt-1 text-xs text-ele-body">
          Week beginning {formatDate(group.weekStarting)}. A rollup for the P&amp;L view, {BASIS_COPY[group.basis]} It is not a third invoice figure: each engineer&apos;s
          invoice is checked against their own figure above.
        </p>
        <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
          {group.members.map((mmb) => (
            <li key={mmb.engineerId}>
              <span className="text-ele-strong">{mmb.engineerName}</span> <span className="text-ele-body">{formatGbp(mmb.totalPence)}</span>
              {mmb.unpriced && <span className="text-xs text-[var(--ele-amber-text-deep)]"> (no rate card)</span>}
              {!mmb.unpriced && mmb.reviewCount > 0 && (
                <span className="text-xs text-[var(--ele-amber-text-deep)]">
                  {" "}
                  ({mmb.reviewCount} to review)
                </span>
              )}
              <span className="text-xs text-ele-body opacity-70"> · {mmb.status.toLowerCase()}</span>
            </li>
          ))}
          <li>
            <span className="text-ele-strong">Combined</span>{" "}
            <span className="font-heading text-ele-green" data-testid="pay-combined">
              {formatGbp(group.combinedTotalPence)}
            </span>
          </li>
        </ul>
      </div>
      )}

      {pay.rateCardEngineer != null && (
        <div className="mt-4 border-t border-ele-purple-accent/60 pt-3">
          <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">Xero</p>
          {journalDrafted ? (
            <>
              <p className="mt-1 text-sm text-ele-body">
                Drafted in Xero as a zero-net journal for{" "}
                <span className="text-ele-strong">{timesheet.xeroJournalAmountPence != null ? formatGbp(timesheet.xeroJournalAmountPence) : "the figure at the time"}</span>
                {timesheet.xeroJournalDraftedAt ? ` on ${formatDate(timesheet.xeroJournalDraftedAt)}` : ""}. It reaches the P&amp;L once the accountant posts it in
                Xero.
                {journalStale && (
                  <span role="status" className="mt-1 block text-[var(--ele-amber-text-deep)]">
                    This week now computes to {formatGbp(pay.totalPence)}, which is not what the journal carries. Void the journal in Xero, release it here, and
                    draft it again if the new figure is the right one.
                  </span>
                )}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <ConfirmButton
                  onConfirm={() => void clearJournal()}
                  busy={clearBusy}
                  busyLabel="Checking Xero..."
                  label="The journal was voided in Xero: release it here"
                  confirmLabel="Check Xero and release the journal record?"
                  className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                />
                <span className="text-xs text-ele-body">Released only once Xero confirms the journal is voided or deleted.</span>
              </div>
              {clearError && (
                <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
                  {clearError}
                </p>
              )}
            </>
          ) : journalState === "pending" ? (
            <p className="mt-1 text-sm text-ele-body">A journal is being drafted for this week right now. Reload in a minute.</p>
          ) : journalState === "stale_pending" ? (
            <>
              <p role="status" className="mt-1 text-sm text-[var(--ele-amber-text-deep)]">
                A previous attempt to draft the journal did not complete. Nothing was recorded, so it can be cleared and tried again.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <ConfirmButton
                  onConfirm={() => void clearJournal()}
                  busy={clearBusy}
                  busyLabel="Clearing..."
                  label="Clear the stuck attempt"
                  confirmLabel="Clear the stuck attempt so the journal can be drafted again?"
                  className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                />
              </div>
              {clearError && (
                <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
                  {clearError}
                </p>
              )}
            </>
          ) : journalDone ? (
            <p className="mt-1 text-sm text-ele-body" role="status">
              Drafted in Xero as a zero-net journal for <span className="text-ele-strong">{formatGbp(journalDone.amountPence)}</span>. It reaches the P&amp;L once the
              accountant posts it in Xero.
            </p>
          ) : (
            <>
              <p className="mt-1 text-sm text-ele-body">
                Send this figure to Xero as a zero-net DRAFT journal (the labour cost and a matching recharge line), so it shows in the P&amp;L once posted without
                changing what is billed or paid.
                {!canDraftJournal
                  ? " Approve the week first: the figure is not final until then."
                  : pay.totalPence <= 0
                    ? " This week comes to nothing under the rate card, so there is no journal to draft."
                    : ""}
              </p>
              {journalPrompt && (
                <div role="status" className="mt-2 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-3 text-sm text-ele-strong">
                  <p>{journalPrompt.message}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <ConfirmButton
                      onConfirm={() => void draftJournal(true)}
                      busy={journalBusy}
                      busyLabel="Drafting..."
                      label="Draft anyway"
                      confirmLabel={`Draft the journal at ${formatGbp(pay.totalPence)} with ${journalPrompt.count} item${journalPrompt.count === 1 ? "" : "s"} still to review?`}
                      className="rounded-ele border border-ele-amber-line px-4 py-2 font-menu text-xs tracking-wide text-[var(--ele-amber-text-deep)] transition hover:bg-ele-inset"
                    />
                    <button type="button" onClick={() => setJournalPrompt(null)} className="font-menu text-xs tracking-wide text-ele-body hover:text-ele-green">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {!journalPrompt && canDraftJournal && pay.totalPence > 0 && (
                <div className="mt-2">
                  <ConfirmButton
                    onConfirm={() => void draftJournal(false)}
                    busy={journalBusy}
                    busyLabel="Drafting..."
                    label="Draft Xero journal"
                    confirmLabel={`Draft a zero-net journal in Xero for ${formatGbp(pay.totalPence)}?`}
                    disabled={!timesheet.timesheetId}
                    className="rounded-ele border border-ele-blue px-4 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset disabled:opacity-40"
                  />
                </div>
              )}
              {journalError && (
                <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
                  {journalError}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

function LineRow({ line }: { line: TimesheetPayLine }) {
  const zero = line.kind === "travel_unpaid" || line.kind === "tech_visit_unpaid";
  const negative = line.amountPence < 0;
  return (
    <li className="flex flex-col gap-0.5 py-1.5 text-sm sm:flex-row sm:items-baseline sm:justify-between">
      <div className="min-w-0">
        <span className="text-ele-strong">{line.label}</span>
        {line.jobLabel && <span className="text-ele-body"> · {line.jobLabel}</span>}
        {line.note && <span className="block text-xs text-ele-body">{line.note}</span>}
      </div>
      <div className="shrink-0 text-right">
        {line.minutes != null && <span className="text-ele-body">{formatDurationHm(line.minutes)} </span>}
        {lineRate(line) && <span className="text-xs text-ele-body">at {lineRate(line)} </span>}
        <span className={zero ? "text-ele-body" : negative ? "text-[var(--ele-amber-text-deep)]" : "text-ele-strong"}>{formatGbp(line.amountPence)}</span>
      </div>
    </li>
  );
}

// The un-booked two-man flag carries its own fix, because the sentence it shows says "mark the job
// as such" and a flag with no way to act on it in place is a nag. Recording the booking is an
// audited office act on the job (PATCH /api/schedule/jobs/:id/two-man), after which the figure is
// recomputed; correcting the entries instead is the other honest resolution and needs no button.
function ExceptionRow({ exception, canEdit, onChanged }: { exception: TimesheetPayException; canEdit: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const offersBooking = exception.jobId != null && (exception.code === "two_man_not_booked" || exception.code === "two_man_not_booked_other");

  async function markTwoMan() {
    if (!exception.jobId) return;
    setBusy(true);
    setError(null);
    try {
      await api.schedule.twoMan(exception.jobId, true);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : "Could not record the booking. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="text-ele-strong">
      {exception.date && <span className="text-ele-body">{formatWeekdayShort(exception.date)}: </span>}
      {exception.minutes != null && <span className="text-ele-body">{formatDurationHm(exception.minutes)}. </span>}
      {exception.message}
      {offersBooking && (
        <span className="mt-1 flex flex-wrap items-center gap-3">
          <ConfirmButton
            onConfirm={() => void markTwoMan()}
            busy={busy}
            busyLabel="Recording..."
            label="It was booked two-man"
            confirmLabel={`Record ${exception.jobLabel ?? "this job"} as booked two-man?`}
            disabled={!canEdit}
            className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-40"
          />
          {!canEdit && <span className="text-xs text-ele-body">Reopen the week to record a booking.</span>}
          {error && (
            <span role="alert" className="text-xs text-ele-danger">
              {error}
            </span>
          )}
        </span>
      )}
    </li>
  );
}
